import {Suspense, useCallback, useRef, useState} from 'react'
import {AlertIcon, PencilIcon} from '@primer/octicons-react'
import {Button, Spinner} from '@primer/react'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {ForYouItem} from '../shared/ForYouItem'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {ReviewMenu, type ReviewMenuOverlayMode} from '../../../review-menu/ReviewMenu'
import {useLayoutRouteData} from '../../../../page-data/loaders/use-layout-route-data'
import {useFeatureState} from '../../../../hooks/use-feature-state'
import {FeatureState} from '../../../../page-data/payloads/header'
import {usePendingReviews} from '../../../../stores/layout-selectors'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import {PendingCommentsPreview} from './PendingCommentsPreview'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'

export function ReviewPending() {
  const {pullRequest, repository, stack} = useLayoutRouteData()

  // Get the pending review from the layout store so the state remains synced across tab navs
  const pendingReview = usePendingReviews()
  const pendingCommentCount = pendingReview?.pendingReviewIDs?.length || 0

  const currentUserLogin = getCurrentUserLogin()
  const isPullRequestStacksEnabled = useFeatureState('pullRequestStacks') === FeatureState.Enabled
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const [overlayMode, setOverlayMode] = useState<ReviewMenuOverlayMode>('closed')
  const anchorRef = useRef<HTMLButtonElement>(null)

  const handleOpen = useCallback(() => {
    sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
      item_name: 'pending-review',
      action_name: 'finish_review',
    })
    setOverlayMode('dialog')
  }, [sendPullRequestAnalyticsEvent])

  if (!pendingReview?.id || !currentUserLogin) return null

  const description =
    pendingCommentCount > 0
      ? `${pendingCommentCount} pending comment${pendingCommentCount === 1 ? '' : 's'}`
      : undefined

  return (
    <>
      <ForYouItem
        leadingVisual={<ForYouLeadingIcon icon={PencilIcon} />}
        title="Your review is in progress"
        itemName="pending-review"
        description={description}
        actionSlot={
          <Button ref={anchorRef} variant="primary" size="small" onClick={handleOpen}>
            Finish your review
          </Button>
        }
      >
        {pendingCommentCount > 0 && (
          <ErrorBoundary
            boundaryName="ForYouReviewPendingCommentsPreview"
            fallback={
              <span className="fgColor-muted">
                <AlertIcon className="mr-2" /> Failed to load pending comments
              </span>
            }
          >
            <Suspense fallback={<Spinner />}>
              <PendingCommentsPreview />
            </Suspense>
          </ErrorBoundary>
        )}
      </ForYouItem>
      <ReviewMenu
        currentUserLogin={currentUserLogin}
        pullRequest={pullRequest}
        headRefOid={pullRequest.headSha}
        repository={repository}
        overlayMode={overlayMode}
        onOverlayModeChange={setOverlayMode}
        anchorRef={anchorRef}
        overlayPortal="external"
        redirectOnMutation
        stackBaseBranch={isPullRequestStacksEnabled ? stack?.baseBranch : undefined}
      />
    </>
  )
}
